import type {SenseAction} from 'src/action-creators/types';
import type {BrandInfo} from 'src/types/messaging-secure-upload';
import {
  RECEIVE_UPLOADED_FILES,
  RECEIVE_UPLOADED_FILES_ERROR,
  RECEIVE_UPLOAD_RESPONSE,
  RECEIVE_UPLOAD_ERROR,
  RECEIVE_THREAD_UPLOADS,
  RECEIVE_THREAD_UPLOADS_ERROR,
  RECEIVE_MORE_THREAD_UPLOADS,
  CLEAR_THREAD_UPLOADS,
  type MessagingSecureUploadAction,
} from 'src/action-creators/messaging-secure-upload';


export type State = {
  uploadedFiles: Array<{
    contentType: string;
    fileName: string;
  }>;
  brandInfo: BrandInfo;
  threadUploads: Array<{
    id: string;
    fileName: string;
    fileSize: number;
    contentType: string;
    s3Url: string;
    threadId: string;
    timeCreated: string;
  }>;
  isLoading: boolean;
  error: string | null | undefined;
  isTokenExpired: boolean;
  hasMoreThreadUploads: boolean;
};

const initialState: State = {
  uploadedFiles: [],
  brandInfo: null,
  threadUploads: [],
  isLoading: false,
  error: null,
  isTokenExpired: false,
  hasMoreThreadUploads: true,
};

export default function reduce(
  state: State | null | undefined = initialState,
  action: MessagingSecureUploadAction,
): State {
  if (!state) {
    return initialState;
  }
  switch (action.type) {
    case RECEIVE_UPLOADED_FILES: {
      return {
        ...state,
        uploadedFiles: action.payload.files,
        brandInfo: action.payload.brandInfo,
        isLoading: false,
        error: null,
        isTokenExpired: false,
        threadUploads: state.threadUploads,
        hasMoreThreadUploads: state.hasMoreThreadUploads,
      };
    }

    case RECEIVE_UPLOADED_FILES_ERROR: {
      const isTokenExpired = action.payload.error === 'API_ERROR';

      return {
        ...state,
        isLoading: false,
        error: action.payload.message || 'Failed to load uploaded files',
        isTokenExpired,
        uploadedFiles: state.uploadedFiles,
        threadUploads: state.threadUploads,
        hasMoreThreadUploads: state.hasMoreThreadUploads,
      };
    }

    case RECEIVE_UPLOAD_ERROR: {
      return {
        ...state,
        isLoading: false,
        error: action.payload.message || 'Failed to upload file',
        isTokenExpired: false,
        uploadedFiles: state.uploadedFiles,
        threadUploads: state.threadUploads,
        hasMoreThreadUploads: state.hasMoreThreadUploads,
      };
    }

    case RECEIVE_THREAD_UPLOADS: {
      return {
        ...state,
        threadUploads: action.payload,
        isLoading: false,
        error: null,
        isTokenExpired: false,
        hasMoreThreadUploads: action.payload.length === 10,
        uploadedFiles: state.uploadedFiles,
      };
    }

    case RECEIVE_MORE_THREAD_UPLOADS: {
      return {
        ...state,
        threadUploads: [...state.threadUploads, ...action.payload],
        isLoading: false,
        error: null,
        isTokenExpired: false,
        hasMoreThreadUploads: action.payload.length === 10,
        uploadedFiles: state.uploadedFiles,
      };
    }

    case RECEIVE_THREAD_UPLOADS_ERROR: {
      return {
        ...state,
        isLoading: false,
        error: action.payload.message || 'Failed to load thread uploads',
        isTokenExpired: false,
        uploadedFiles: state.uploadedFiles,
        threadUploads: state.threadUploads,
        hasMoreThreadUploads: state.hasMoreThreadUploads,
      };
    }

    case CLEAR_THREAD_UPLOADS: {
      return {
        ...state,
        threadUploads: [],
        hasMoreThreadUploads: true,
        isLoading: false,
        error: null,
        uploadedFiles: state.uploadedFiles,
        isTokenExpired: state.isTokenExpired,
      };
    }

    default:
      return state;
  }
}
